import { useMutation } from '@apollo/client'
import clsx from 'clsx'
import { FormikErrors, useFormik } from 'formik'
import { useContext, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { UPDATE_BG_ACCOUNT, UPDATE_USER } from '../../graphql/mutation'
import { GET_BG_ACCOUNT_BY_ID, GET_USER_BY_ID } from '../../graphql/query'
import { toAbsoluteUrl } from '../../helpers'
import * as Yup from 'yup'

import { toast } from 'react-toastify'
import UserContext from '../../context/UserContext'

interface IProps {
  closeBusinessModal?: () => void
  loading: any
  error: any
  data: any
  formData: any
  setFormData: any
}

const OnboardingSchema = Yup.object().shape({
  CIN: Yup.string(),
  PAN: Yup.string(),
  TAN: Yup.string(),
  GSTIN: Yup.string()
})

export const LegalInformationPage: React.FC<IProps> = ({
  closeBusinessModal,
  loading,
  error,
  data,
  formData,
  setFormData
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [updateUser] = useMutation(UPDATE_USER, {
    refetchQueries: [
      {
        query: GET_USER_BY_ID,
        context: {
          headers: {
            'access-control-allow-methods': 'Profile'
          }
        }
      }
    ],
    context: {
      headers: {
        'access-control-allow-methods': 'Profile'
      }
    }
  })

  const [updateBgAccount] = useMutation(UPDATE_BG_ACCOUNT, {
    refetchQueries: [{ query: GET_BG_ACCOUNT_BY_ID }]
  })
  const { currentUser } = useContext(UserContext)

  const navigate = useNavigate()

  const initialValues = {
    CIN: !!data && !loading && !error && data.getBgAccountById.CIN ? data.getBgAccountById.CIN : '',

    PAN: !!data && !loading && !error && data.getBgAccountById.PAN ? data.getBgAccountById.PAN : '',

    TAN: !!data && !loading && !error && data.getBgAccountById.TAN ? data.getBgAccountById.TAN : '',

    GSTIN:
      !!data && !loading && !error && data.getBgAccountById.GSTIN ? data.getBgAccountById.GSTIN : ''
  }

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: OnboardingSchema,
    onSubmit: (values) => {
      const updatedFormData = {
        ...formData,
        CIN: values.CIN,
        PAN: values.PAN,
        TAN: values.TAN,
        GSTIN: values.GSTIN
      }

      setFormData(updatedFormData)
      updateBgAccount({
        variables: {
          input: formData
        }
      })
        .then((res: any) => {
          console.log(res)
          if (res.data.updateBgAccount.status === 200) {
            updateUser({
              variables: {
                input: {
                  id: parseInt(currentUser.id),
                  name: currentUser.name,
                  onboardingDone: true,
                  UserRoles: [{ roleId: currentUser.UserRole[0].roleId }]
                }
              }
            }).then((res1: any) => {
              if (res1.data.updateUser.status >= 200 && res1.data.updateUser.status < 300) {
                toast.success(res.data.updateBgAccount.message)

                console.log(res1)
                navigate('/bots/all-bots')
              }
            })
          } else {
            toast.error(res.data.updateBgAccount.message)
          }
        })
        .catch((err) => {
          console.log(err)
        })
    }
  })

  if (loading) {
    setIsLoading(loading)
  }

  if (error) {
    return <p>error</p>
  }

  const getErrorMessage = (error: string | string[] | FormikErrors<any> | FormikErrors<any>[]) => {
    if (typeof error === 'string') {
      return error
    } else if (Array.isArray(error)) {
      return error.join(', ')
    } else {
      return JSON.stringify(error)
    }
  }
  return (
    <div
      className='d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed'
      style={{
        backgroundImage: `url(${toAbsoluteUrl('/media/illustrations/sketchy-1/14.png')})`
      }}
    >
      <div className='d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20'>
        <div className='w-lg-500px bg-white rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <form
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            id='kt_login_signup_form'
            onSubmit={formik.handleSubmit}
          >
            {!closeBusinessModal && (
              <div className='mb-10 text-center'>
                <h2 className='text-dark mb-4'> Legal Information Details</h2>
                {/* <p className='text-muted fs-5'>Enter details of the new buisness account </p> */}
              </div>
            )}

            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>CIN :</label>
              <input
                placeholder='CIN number'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('CIN')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.CIN && formik.errors.CIN
                  },
                  {
                    'is-valid': formik.touched.CIN && !formik.errors.CIN
                  }
                )}
              />
              {formik.touched.CIN && formik.errors.CIN && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.CIN)}</span>
                  </div>
                </div>
              )}
            </div>
            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>PAN :</label>
              <input
                placeholder='PAN number'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('PAN')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.PAN && formik.errors.PAN
                  },
                  {
                    'is-valid': formik.touched.PAN && !formik.errors.PAN
                  }
                )}
              />
              {formik.touched.PAN && formik.errors.PAN && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.PAN)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>TAN :</label>
              <input
                placeholder='TAN number'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('TAN')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.TAN && formik.errors.TAN
                  },
                  {
                    'is-valid': formik.touched.TAN && !formik.errors.TAN
                  }
                )}
              />
              {formik.touched.TAN && formik.errors.TAN && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.TAN)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='fv-row mb-7'>
              <label className='form-label fw-bolder text-dark fs-6'>GSTIN :</label>
              <input
                placeholder='GSTIN number'
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('GSTIN')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.GSTIN && formik.errors.GSTIN
                  },
                  {
                    'is-valid': formik.touched.GSTIN && !formik.errors.GSTIN
                  }
                )}
              />
              {formik.touched.GSTIN && formik.errors.GSTIN && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{getErrorMessage(formik.errors.GSTIN)}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='text-center'>
              <button
                type='submit'
                id='kt_sign_up_submit'
                className='btn btn-lg btn-primary w-100 mb-5'
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {!isLoading && <span className='indicator-label'>Next</span>}
                {isLoading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Please wait...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>

              {!closeBusinessModal && (
                <Link to='/onboarding/organization-details'>
                  <button
                    type='button'
                    id='kt_login_signup_form_cancel_button'
                    className='btn btn-lg btn-secondary w-100 mb-5'
                  >
                    Previous
                  </button>
                </Link>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
export default LegalInformationPage
